import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchLanguages } from '@eliadmin/core/src/api/general/general-api';
import { JobportalAddLanguageField as Field } from '@eliadmin/core/src/components/jobportal/jobportal-create-curriculum/jobportal-add-language/jobportal-add-language-form-fields';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Form } from '@eliadmin/core/src/components/ui/form';
import {
  ResponsiveDialogContent,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
  useResponsiveDialogStore,
} from '@eliadmin/core/src/components/ui/responsive-dialog';
import { Separator } from '@eliadmin/core/src/components/ui/separator';
import {
  useFormValidationMessages,
  type FormValidationMessages,
} from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const addLanguageSchema = (VM: FormValidationMessages) =>
  z.object({
    id: z.number().or(z.string()),
    language: z.string(),
    level: z.enum([
      LanguageLevels.A1,
      LanguageLevels.A2,
      LanguageLevels.B1,
      LanguageLevels.B2,
      LanguageLevels.C1,
      LanguageLevels.C2,
    ]),
    locale: z.string(),
  });

export type AddLanguageSchemaType = z.infer<ReturnType<typeof addLanguageSchema>>;

type JobportalAddLanguageDialogProps = {
  onSubmit?: (data: AddLanguageSchemaType) => void;
  onEdit?: (data: AddLanguageSchemaType) => void;
  dataToEdit?: AddLanguageSchemaType;
  addedLanguages?: AddLanguageSchemaType[];
};

export enum LanguageLevels {
  A1 = 'A1',
  A2 = 'A2',
  B1 = 'B1',
  B2 = 'B2',
  C1 = 'C1',
  C2 = 'C2',
}

const languageLevelsValues = [
  { label: 'text982', value: LanguageLevels.A1 },
  { label: 'text983', value: LanguageLevels.A2 },
  { label: 'text984', value: LanguageLevels.B1 },
  { label: 'text985', value: LanguageLevels.B2 },
  { label: 'text986', value: LanguageLevels.C1 },
  { label: 'text987', value: LanguageLevels.C2 },
];

export const JobportalAddLanguageDialog = ({
  onSubmit,
  onEdit,
  dataToEdit,
  addedLanguages,
}: JobportalAddLanguageDialogProps) => {
  const { t } = useTranslations();

  const close = useResponsiveDialogStore(s => s.close);

  const { formValidationMessages } = useFormValidationMessages(t);

  const form = useForm<z.infer<ReturnType<typeof addLanguageSchema>>>({
    defaultValues: onEdit
      ? {
          ...dataToEdit,
          id: dataToEdit?.id,
        }
      : {
          language: '',
          level: LanguageLevels.A1,
          locale: '',
        },
    resolver: zodResolver(addLanguageSchema(formValidationMessages)),
  });

  const { data: languages } = useQuery({
    queryKey: [CacheKey.LANGUAGES],
    queryFn: () =>
      fetchLanguages({
        query: {
          results: 100,
        },
      }).then(res => {
        return { data: res.data.filter(lang => !addedLanguages?.find(l => l.id === lang.id)) };
      }),
  });

  const handleSubmit = form.handleSubmit(async data => {
    close(JobportalAddLanguageDialog.id);
    onSubmit &&
      onSubmit({
        ...data,
        id: data.id,
        language: languages?.data.find(lang => String(lang.id) === String(data.id))?.name || '',
        locale: languages?.data.find(lang => String(lang.id) === String(data.id))?.locale || '',
      });

    onEdit &&
      onEdit({
        ...data,
      });
  });

  return (
    <>
      <ResponsiveDialogContent
        dialogProps={{
          className:
            'max-w-screen max-h-[94vh] w-[680px] overflow-y-auto sm:max-w-[600px] md:max-w-[680px] lg:max-w-[680px] ea-jobportal-add-skill-dialog__content ea-jobportal-dialog__content',
        }}
        drawerProps={{
          className:
            'pb-6 max-h-screen ea-jobportal-add-skill-dialog__content ea-jobportal-dialog__content',
        }}
      >
        <form onSubmit={handleSubmit} className={cn('flex flex-col gap-6 overflow-auto px-4', '')}>
          <Form {...form}>
            <ResponsiveDialogHeader
              className={cn(
                'ea-jobportal-add-language-dialog__header ea-add-language-dialog__header ea-jobportal-dialog__header',
              )}
            >
              <ResponsiveDialogTitle
                className={cn(
                  'font-semibold',
                  'ea-jobportal-add-language-dialog__header-title ea-jobportal-dialog__header-title',
                )}
              >
                {t('text988')}
              </ResponsiveDialogTitle>
            </ResponsiveDialogHeader>
            <div
              className={cn(
                'grid grid-cols-12 gap-x-4 gap-y-3',
                'ea-jobportal-dialog__form-content ea-jobportal-add-language-dialog__form-content',
              )}
            >
              <div className="col-span-12">
                <Field
                  type="select"
                  options={
                    languages?.data?.map(lang => ({
                      value: Number(lang.id),
                      label: t(lang.name),
                    })) || []
                  }
                  control={form.control}
                  name="id"
                  label={t('text989')}
                  required
                />
              </div>
              <div className="col-span-12 flex">
                <Field
                  control={form.control}
                  name="level"
                  type="radio"
                  label={t('text990')}
                  className={{
                    radio: {
                      radioGroupItemsWrapper: 'flex-col sm:flex-row',
                    },
                  }}
                  options={languageLevelsValues.map(g => ({
                    value: g.value,
                    label: t(g.label),
                  }))}
                />
              </div>
              <Separator
                className={cn(
                  'col-span-12 my-2 h-[1px] bg-border',
                  'ea-jobportal-dialog__separator ea-jobportal-add-language-dialog__separator',
                )}
              />
              <div
                className={cn(
                  'col-span-12 flex items-center justify-end gap-2',
                  'ea-jobportal-dialog__buttons-wrapper ea-jobportal-add-language-dialog__buttons-wrapper',
                )}
              >
                <Button
                  variant={'default-alt'}
                  className={cn(
                    '',
                    'ea-jobportal-dialog__cancel-button ea-jobportal-add-language-dialog__cancel-button',
                  )}
                  onClick={() => close(JobportalAddLanguageDialog.id)}
                >
                  {t('text377')}
                </Button>
                <Button
                  className={cn(
                    '',
                    'ea-jobportal-dialog__submit-button ea-jobportal-add-skill-dialog__submit-button',
                  )}
                  onClick={() => {
                    handleSubmit();
                  }}
                >
                  {t('text201')}
                </Button>
              </div>
            </div>
          </Form>
        </form>
      </ResponsiveDialogContent>
    </>
  );
};

JobportalAddLanguageDialog.id = 'JOBPORTAL_ADD_LANGUAGE_DIALOG';
